import $ from 'jquery';

import {BenchlingSelection} from 'notebook/mediocre/selection/SelectionStore';
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {rearmSpellCheck} from 'notebook/mediocre/selection/text/SpellCheckRearm';
import * as logging from 'util/Logging';

/*
 * This file was created to allow us to access the same functionality as EditableListMixin
 * in React classes in TS refactors. These classes will need to manually call the relevant
 * method in their appropriate lifecycle function. Classic components can still continue to
 * use the EditableListMixin. Looking forward, this file will be replaced as we move towards
 * modern HOCs and hooks.
 */

export interface EditableListProps {
  metadataForRestore?: {selection: BenchlingSelection | null};
}

export function editableListWillMount(setSelection: (selection: BenchlingSelection | null) => void): void {
  clearSelection(setSelection);
}

export function clearSelection(setSelection: (selection: BenchlingSelection | null) => void): void {
  setSelection(null);
}

/**
 * Helper method to find the child DOM node that is of the form <div data-cid="{cid}">...</div> used for
 * restoring the selection after re-render.
 */
function _getChildNodeWithCid(cid: string, rootNode: Element): Element {
  const selector = `[data-cid='${cid}']`;

  // Typically the active element is the node being selected, so let's avoid searching for it by data-cid,
  // which can be expensive if the component has many nodes.
  const activeNode = document.activeElement;
  if (activeNode != null && $.contains(rootNode, activeNode) && $(activeNode).is(selector)) {
    return activeNode;
  }

  // Otherwise, first check the root.
  if ($(rootNode).is(selector)) {
    return rootNode;
  }

  // And then every descendant of root.
  return $(rootNode).find(selector)[0];
}

function _getChildNodeEditable(cid: string, rootNode: Element): Element {
  const childNodeWithCid = _getChildNodeWithCid(cid, rootNode);
  if ($(childNodeWithCid).is('[contentEditable]')) {
    return childNodeWithCid;
  } else {
    return $(childNodeWithCid).find('input, textarea')[0];
  }
}

/**
 * Called on re-render that attempts to set the DOM selection to the node specified by either
 * props.metadataForRestore or explicitly specified via setSelection.
 */
function _restoreSelection(rootNode: Element, selection?: BenchlingSelection | null): void {
  if (selection == null) {
    return;
  }
  const {cid, start, end} = selection;
  const editable = _getChildNodeEditable(cid, rootNode);
  if (editable == null) {
    return;
  }
  selutil.restoreSelection(editable, {start, end});
  // Re-run spell check after every keystroke event in a note-part (enter, arrow-selection, typing)
  // since re-render will kill Blink's markers.
  rearmSpellCheck(editable, start === end);
}

export function editableListDidMount(
  rootNode: Element,
  currentProps: EditableListProps,
  setSelection: (selection: BenchlingSelection | null) => void
): void {
  const selection =
    currentProps.metadataForRestore != null ? currentProps.metadataForRestore.selection : null;
  setSelection(selection);
  _trySetSelection(rootNode, selection, setSelection);
}

export function editableListDidUpdate(
  rootNode: Element,
  prevProps: EditableListProps,
  currentProps: EditableListProps,
  selection: BenchlingSelection | null,
  setSelection: (selection: BenchlingSelection | null) => void
): void {
  // If the parent has pushed a new selection to restore, try restoring.
  const oldSelection = prevProps.metadataForRestore != null ? prevProps.metadataForRestore.selection : null;
  const newSelection =
    currentProps.metadataForRestore != null ? currentProps.metadataForRestore.selection : null;
  if (oldSelection !== newSelection) {
    setSelection(newSelection);
    _trySetSelection(rootNode, newSelection, setSelection);
  } else {
    _trySetSelection(rootNode, selection, setSelection);
  }
}

function _trySetSelection(
  rootNode: Element,
  selection: BenchlingSelection | null,
  setSelection: (selection: BenchlingSelection | null) => void
): void {
  // Try, and give up if it doesn't work.
  try {
    _restoreSelection(rootNode, selection);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    logging.warn(e);
  } finally {
    clearSelection(setSelection);
  }
}
